<template>
  <v-container class="subscription-view mt-n5 py-8 px-6">
    <!-- Header Section -->
    <v-row class="py-4 align-center">
      <v-col cols="12" md="8" sm="12" class="text-center text-md-left mb-4 mb-md-0">
        <h5 class="text-headline-small font-weight-bold text-grey-darken-4 mb-2">
          Pricing Plans
        </h5>
        <p class="text-body-medium text-grey-darken-2 mb-0">
          Choose the plan that's right for you. Flexible billing, choose
          monthly or save with yearly.</p>
        <p class="text-body-small text-grey-darken-1 mb-0">
          All prices are excluding tax; applicable tax is added at checkout based on your location.</p>
      </v-col>
      <v-col cols="12" md="4" sm="12"
        class="d-flex justify-center justify-md-end align-center mt-2 mt-md-0 text-center text-md-right">
        <!-- Billing Toggle -->
        <div class="d-flex rounded-lg border border-grey-lighten-2 justify-space-between align-center px-2 py-1"
          style="max-width: 320px;">
          <v-btn :class="[
            'flex-grow-1 text-none font-weight-bold px-5 rounded-s-lg no-hover',
            planType.isMonthly ? 'text-white' : 'text-grey-darken-4'
          ]" rounded="0" height="36" :color="planType.isMonthly ? 'brand-color' : 'transparent'"
            :variant="planType.isMonthly ? 'flat' : 'text'" density="comfortable" @click="planType.isMonthly = true">
            Monthly
          </v-btn>
          <v-btn :class="[
            'flex-grow-1 text-none font-weight-bold px-5 rounded-e-lg no-hover',
            planType.isMonthly ? 'text-grey-darken-4' : 'text-white'
          ]" rounded="0" height="36" :color="planType.isMonthly ? 'transparent' : 'brand-color'"
            :variant="planType.isMonthly ? 'text' : 'flat'" density="comfortable" @click="planType.isMonthly = false">
            Yearly (Save 20%)
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Alert section for success/error messages -->
    <v-row class="py-0 my-0 justify-center">
      <v-col :cols="pricingPlans.length > 1 ? 9 : 12" v-if="alert.show">
        <v-alert :color="alert.isError ? 'danger' : 'success'" variant="tonal" rounded="lg" density="compact"
          :icon="alert.isError ? 'mdi-alert-circle' : 'mdi-check-circle'" class="mb-1 mt-5 text-black">
          <span class="text-black mb-0">{{ alert.message }}</span>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Alert section -->
    <v-row class="justify-center" v-if="!isAdmin && alertConfig">
      <v-col :cols="pricingPlans.length > 1 ? 9 : 12">
        <v-banner color="white" :border="alertConfig.textColor" :class="['border-sm', 'bg-' + alertConfig.bgColor]"
          rounded="lg">
          <v-banner-text class="d-flex flex-column pr-3">
            <span v-if="alertConfig.title" :class="['font-weight-bold span-title', 'text-' + alertConfig.textColor]">{{
              alertConfig.title }}</span>
            <span :class="['span-msg', 'text-' + alertConfig.textColor]" v-html="alertConfig.message"></span>
          </v-banner-text>
          <v-banner-actions v-if="alertConfig.showButton" class="banner-actions">
            <v-btn @click="openManagePlanDialog" variant="flat" size="small" width="180" :color="alertConfig.textColor"
              prepend-icon="mdi-wallet" class="text-none font-weight-bold manage-btn">
              {{ alertConfig.buttonText }}
            </v-btn>
          </v-banner-actions>
        </v-banner>
      </v-col>
    </v-row>

    <!-- Pricing Cards -->
    <v-row class="mt-8 justify-center">
      <v-col v-for="plan in pricingPlans" :key="plan.name" cols="12" sm="6" md="3" class="d-flex mb-6">
        <PCard :plan="plan" :interval="billing.interval" :is-current="plan.isCurrent" :is-popular="plan.isPopular"
          :subscription-status="planHelpers_subscriptionStatus"
          :current-subscription-status="planHelpers_subscriptionStatus" :is-past-due="userMixin_isPastDueUser"
          :is-trialing-this-plan="!hasSubscription && userMixin_inTrial && plan.isCurrent"
          :is-trial-expired-on-this-plan="!hasSubscription && userMixin_trialEnded && plan.isCurrent"
          @select-plan="handleSelectPlan" class="w-100" />
      </v-col>
    </v-row>

    <!-- Comparison Table -->
    <v-row v-if="pricingPlansWithDetails && pricingPlansWithDetails.length > 0" class="mt-8">
      <v-col cols="12" class="text-center text-md-left">
        <h5 class="text-headline-small font-weight-bold text-grey-darken-4 mb-0">Compare all plans</h5>
      </v-col>
      <v-col cols="12">
        <PTable :pricing-plans-with-details="pricingPlansWithDetails" />
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <PDialog v-model="dialog.open" :mode="dialog.mode" :selected-plan-key="dialog.selectedPlanKey" :plan-type="planType"
      @subscription-updated="handleSubscriptionUpdated" @alert="handleAlert" />
  </v-container>
</template>

<script>
import PCard from '@/components/pricing/PCard.vue'
import PTable from '@/components/pricing/PTable.vue'
import PDialog from '@/components/pricing/PDialog.vue'
import userMixin from '@/mixins/user'
import pricingMixin from '@/mixins/pricing'
import pricingHelpersMixin from '@/mixins/pricing/helpers'
import helperMixin from '@/mixins/helper'
import * as actionTypes from '@/store/action-types'
import { syncPlanSubscription } from '@/api/plan'
import { PLAN_ORDER, PRICING_PLANS } from '@/shared/constants/pricing/data'
import { getPlanWithBilling, getBillingCycleConfig, isMostPopularPlan } from '@/shared/constants/pricing/service'

export default {
  name: 'Subscription',
  components: {
    PCard,
    PTable,
    PDialog
  },
  mixins: [userMixin, pricingMixin, pricingHelpersMixin, helperMixin],
  data() {
    return {
      planType: {
        isMonthly: true
      },
      dialog: {
        open: false,
        mode: null,
        selectedPlanKey: null
      },
      alert: {
        show: false,
        message: '',
        isError: false,
        timeout: 5000
      }
    }
  },
  async mounted() {
    if (
      this.user?.plan?.public_id &&
      this.user?.plan?.subscription?.id
    ) {
      try {
        // Refreshes local Billing state in case the latest Paddle webhook arrived late, and
        // reconciles accounts still flagged as Classic locally into their real Billing record
        // (e.g. migrated accounts whose subscription was already cancelled pre-migration).
        await syncPlanSubscription(this.user.plan.public_id)
      } catch (error) {
        console.error('Unable to refresh Paddle Billing subscription:', error)
      }

      await this.refreshUser()
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    billing() {
      // Billing cycle config (monthly/yearly) based on toggle state
      return getBillingCycleConfig(this.planType.isMonthly)
    },
    pricingPlans() {
      const billingKey = this.billing.key
      const currentPublicId = this.user?.plan?.public_id || ''
      return PLAN_ORDER.map(key => {
        const plan = getPlanWithBilling(key, billingKey)
        const publicId = this.planType.isMonthly ? plan.key?.monthly : plan.key?.yearly
        const isCurrent = publicId === currentPublicId
        return {
          ...plan,
          isCurrent,
          isPopular: isMostPopularPlan(key)
        }
      })
    },
    pricingPlansWithDetails() {
      const currentPublicId = this.user?.plan?.public_id || ''
      return PLAN_ORDER.map(planKey => {
        const plan = PRICING_PLANS[planKey]
        const publicIdMonthly = plan.key?.monthly
        const publicIdYearly = plan.key?.yearly
        const isCurrent = currentPublicId === publicIdMonthly || currentPublicId === publicIdYearly
        return {
          name: plan.name,
          key: planKey,
          pricing: plan.pricing,
          comparison: plan.comparison,
          isCurrent,
          isPopular: isMostPopularPlan(planKey)
        }
      })
    },
    alertConfig() {
      if (this.isAdmin) return null

      const status = this.planHelpers_subscriptionStatus
      const subscription = this.user?.plan?.subscription

      // Shared message functions
      const getPausedMessage = () => {
        const pausedFrom = subscription?.paused_from || this.user?.plan?.paused_from
        if (!pausedFrom) {
          return 'Your subscription is currently <strong>paused</strong>. After the grace period you\'ll lose access to your account and all your features. If you want to subscribe again please resume your subscription.'
        }

        // Check if grace period is in the past
        const isExpired = new Date(pausedFrom) < new Date()

        if (isExpired) {
          // Grace already ran out - the "you'll lose access after" warning no longer
          // applies, since that's already happened. Resume is still the valid action.
          return `Your subscription is currently <strong>paused</strong>. Your grace period has already ended on <strong>${this.localeDate(pausedFrom)}</strong>. If you want to subscribe again please <strong>resume</strong> your subscription.`
        }

        return `Your subscription is currently <strong>paused</strong>. Your grace period will expire on <strong>${this.localeDate(pausedFrom)}</strong>. After the grace period you'll lose access to your account and all your features. If you want to subscribe again please <strong>resume</strong> your subscription.`
      }

      const getCancelledMessage = () => {
        const endsAt = subscription?.cancel_scheduled_at || this.user?.plan?.cancel_scheduled_at
        return endsAt
          ? `Your subscription is currently <strong>cancelled</strong>. Your grace period will expire on <strong>${this.localeDate(endsAt)}</strong>. After the grace period you'll lose access to your account and all your features. If you want to keep your subscription please <strong>undo your cancellation</strong> now.`
          : 'Your subscription is currently <strong>cancelled</strong>. After the grace period you\'ll lose access to your account and all your features. If you want to keep your subscription please <strong>undo your cancellation</strong> now.'
      }

      const getExpiredMessage = () => {
        return 'Your account is currently <strong>inactive</strong>. Our plans have now improved with more value, better performance, and more features. To take advantage of the latest improvements, and to ensure you\'re on the plan that best fits your needs, you can choose to any of the new options below at any time.'
      }

      // Shared trial message function
      const getTrialMessage = () => {
        if (!this.userMixin_inTrial) {
          return 'Your trial has <strong>ended</strong>. To keep access to your account and grow your business, our plans have now improved with more value, better performance, and more features. To take advantage of the latest improvements, and to ensure you\'re on the plan that best fits your needs, you can choose any of the options below at any time.'
        }

        const days = this.userMixin_trialDaysLeft
        const daysText = days === 1 ? `${days} Day` : `${days} Days`
        return `You have <strong>${daysText}</strong> left on trial. To keep access to your account and grow your business, our plans have now improved with more value, better performance, and more features. To take advantage of the latest improvements, and to ensure you're on the plan that best fits your needs, you can choose any of the options below at any time.`
      }

      // Shared billing failure message function
      const getBillingFailureMessage = () => {
        return 'The billing on your account <strong>failed</strong> for this month\'s payment. Please contact support at <a class="font-weight-bold text-brand-color" href="mailto:hi@cliqforms.com">hi@cliqforms.com</a> to update your billing.'
      }

      // Alert cases mapping
      const alertCases = {
        // Billing failure is handled by support; no self-service billing action is available.
        billingFailure: {
          condition: () => this.userMixin_isPastDueUser,
          config: {
            bgColor: 'orange-lighten-5',
            textColor: 'accent-color',
            showButton: false,
            title: 'Billing Failure',
            getMessage: getBillingFailureMessage
          }
        },
        // Legacy plan - active (special message with button)
        legacyActive: {
          condition: () => this.planHelpers_isLegacyPlan && status === this.SUBSCRIPTION_STATUS.ACTIVE,
          config: {
            bgColor: 'orange-lighten-5',
            textColor: 'accent-color',
            showButton: true,
            buttonText: 'Manage Current Plan',
            title: 'Legacy Plan Notice',
            message: 'You are currently on one of our <strong>legacy plans</strong>. We\'ve launched our <strong>new plans</strong> with more value, better performance, and more features. Your current plan will remain active, but to take advantage of the latest improvements, and to ensure you\'re on the plan that best fits your needs, you can upgrade or switch to any of the new options below at any time. When you are ready, simply choose the plan that suits you best.'
          }
        },
        // Legacy plan - paused (shared message with button)
        legacyPaused: {
          condition: () => this.planHelpers_isLegacyPlan && this.userMixin_hasPausedSubscription,
          config: {
            bgColor: 'orange-lighten-5',
            textColor: 'accent-color',
            showButton: true,
            buttonText: 'Manage Current Plan',
            title: 'Subscription Paused',
            getMessage: getPausedMessage
          }
        },
        // Legacy plan - cancelled (shared message with button)
        legacyCancelled: {
          condition: () => this.planHelpers_isLegacyPlan && status === this.SUBSCRIPTION_STATUS.DELETED,
          config: {
            bgColor: 'red-lighten-5',
            textColor: 'danger-color',
            showButton: true,
            buttonText: 'Manage Current Plan',
            title: 'Subscription Cancelled',
            getMessage: getCancelledMessage
          }
        },
        // Legacy plan - expired (shared message with button)
        legacyExpired: {
          condition: () => this.planHelpers_isLegacyPlan && this.userMixin_hasActiveFreePlan && this.userMixin_planExpired,
          config: {
            bgColor: 'red-lighten-5',
            textColor: 'danger-color',
            showButton: true,
            buttonText: 'Manage Current Plan',
            title: 'Account Inactive',
            getMessage: getExpiredMessage
          }
        },
        // Non-legacy - paused in grace (shared message, no button)
        pausedGrace: {
          condition: () => !this.planHelpers_isLegacyPlan && this.userMixin_hasPausedSubscription,
          config: {
            bgColor: 'orange-lighten-5',
            textColor: 'accent-color',
            showButton: false,
            title: 'Subscription Paused',
            getMessage: getPausedMessage
          }
        },
        // Non-legacy - cancelled in grace (shared message, no button)
        cancelledGrace: {
          condition: () => !this.planHelpers_isLegacyPlan && this.hasCancelledSubscription,
          config: {
            bgColor: 'red-lighten-5',
            textColor: 'danger-color',
            showButton: false,
            title: 'Subscription Cancelled',
            getMessage: getCancelledMessage
          }
        },
        // Trial (shared message, no button for both) - calm brand blue while still
        // trialing (not a problem state), switching to the same accent orange every other
        // "needs your attention" case uses (paused, legacy, billing failure) once expired.
        trialActive: {
          condition: () => this.userMixin_hasActiveFreeTrialPlan && !this.userMixin_planExpired,
          config: {
            bgColor: this.userMixin_inTrial ? 'brand-color-lighten' : 'orange-lighten-5',
            textColor: this.userMixin_inTrial ? 'brand-color' : 'accent-color',
            showButton: false,
            title: this.userMixin_inTrial ? '' : 'Trial Expired',
            getMessage: getTrialMessage
          }
        },
        // Account expired - non-legacy (shared message, no button)
        accountExpired: {
          condition: () => !this.planHelpers_isLegacyPlan && this.userMixin_hasActiveFreePlan && this.userMixin_planExpired,
          config: {
            bgColor: 'red-lighten-5',
            textColor: 'danger-color',
            showButton: false,
            title: 'Account Inactive',
            getMessage: getExpiredMessage
          }
        }
      }

      // Find matching case
      for (const [key, alertCase] of Object.entries(alertCases)) {
        if (alertCase.condition()) {
          const config = { ...alertCase.config }
          // Resolve message if it's a function
          if (config.getMessage) {
            config.message = config.getMessage()
            delete config.getMessage
          }
          // Resolve showButton if it's a function
          if (typeof config.showButton === 'function') {
            config.showButton = config.showButton()
          }
          return config
        }
      }

      return null
    }
  },
  methods: {
    handleSelectPlan(plan) {
      // Get the public_id from plan key based on billing cycle
      const publicId = this.planType.isMonthly ? plan?.key?.monthly : plan?.key?.yearly
      const currentPublicId = this.user?.plan?.public_id || ''

      const isManage = publicId === currentPublicId
      this.dialog.selectedPlanKey = isManage ? null : publicId
      this.dialog.mode = isManage ? this.DIALOG_MODES.MANAGE : this.DIALOG_MODES.CHOOSE
      this.dialog.open = true
    },
    openManagePlanDialog() {
      this.dialog.selectedPlanKey = null
      this.dialog.mode = this.DIALOG_MODES.MANAGE
      this.dialog.open = true
    },
    handleAlert({ message, isError = false, timeout = 5000 }) {
      this.alert = {
        show: true,
        message,
        isError,
        timeout
      }
      setTimeout(() => {
        this.alert.show = false
      }, timeout)
    },
    // Reloads the authenticated user so pricing cards reflect provider-side changes.
    async refreshUser() {
      const response = await this.$store.dispatch(actionTypes.FETCH_USER, { userId: this.user?.id })
      const user = response?.data?.data
      if (user) {
        await this.$store.dispatch(actionTypes.UPDATE_USER_LOCAL, user)
        localStorage.setItem('user', JSON.stringify(user))
      }
    },
    // Re-renders pricing after pause/cancel/update/undo actions finish.
    async handleSubscriptionUpdated() {
      // Refresh user data and force component update after subscription changes
      await this.refreshUser()
      this.$forceUpdate() // Force update to refresh computed properties
    }
  }
}
</script>
